import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';

const CuetUgMockTestPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const handleStart = () => user ? navigate('/test-zone') : navigate('/login');

  const faqs = [
    { q: 'Is CUET UG mock test free?', a: 'Yes, you can attempt our CUET UG mock tests for free with basic access.' },
    { q: 'Are these tests based on the latest CUET pattern?', a: 'Yes, all tests are designed according to the latest CUET UG syllabus and exam format.' },
    { q: 'Will I get my score after the test?', a: 'Yes, you will receive instant results along with detailed performance analysis.' },
    { q: 'How many mock tests should I take?', a: 'Ideally 10–15 full-length mock tests along with regular subject-wise practice tests.' },
    { q: 'Do I need to download anything?', a: 'No, all tests are available online in a simulator — no downloads required.' },
  ];

  const testTypes = [
    { icon: '📘', title: 'Full-Length Mock Tests', desc: 'Complete exam simulation replicating the real CUET UG interface and time limits', badge: 'Most Effective' },
    { icon: '📗', title: 'Subject-Wise Tests', desc: 'Focus on specific subjects — Language, Domain, or General Test sections', badge: 'Popular' },
    { icon: '📙', title: 'Sectional Practice Tests', desc: 'Practice individual sections to target weak areas and improve accuracy', badge: 'Targeted' },
  ];

  const features = [
    { icon: '🎯', title: 'Real Exam Simulator', desc: 'Experience the actual CUET exam interface' },
    { icon: '📚', title: 'Latest Syllabus Coverage', desc: 'Based on updated CUET UG pattern by NTA' },
    { icon: '⚡', title: 'Instant Score & Analysis', desc: 'Know your strengths & weak areas immediately' },
    { icon: '⏱️', title: 'Time-Based Practice', desc: 'Improve speed & accuracy under pressure' },
    { icon: '👨‍🏫', title: 'Expert Designed Tests', desc: 'Created by subject specialists' },
    { icon: '📊', title: 'Performance Tracking', desc: 'Monitor progress across all your attempts' },
  ];

  return (
    <>
    <Helmet>
        <title>CUET UG Mock Test Free | Attempt Online Test Series with Score</title>
        <meta name="description" content="Attempt CUET UG mock test free online with real exam simulator. Get instant score, analysis & improve your preparation. Start now!" />
        <meta name="keywords" content="CUET UG mock test free, CUET mock test online, CUET UG practice test, CUET exam simulator, free CUET test" />
        <link rel="canonical" href="https://besst.in/cuet-ug-mock-test" />
        {/* Open Graph */}
        <meta property="og:title" content="CUET UG Mock Test Free | Attempt Online Test Series with Score" />
        <meta property="og:description" content="Attempt CUET UG mock test free online with real exam simulator. Get instant score, analysis & improve your preparation." />
        <meta property="og:url" content="https://besst.in/cuet-ug-mock-test" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="BESST" />
      </Helmet>

    <div className="min-h-screen pt-32 pb-20">

      {/* Hero */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
        <div className="text-center max-w-4xl mx-auto">
          <div className="hero-badge mx-auto mb-6">🧪 Free Mock Tests</div>
          <h1 className="text-4xl lg:text-6xl font-extrabold gradient-text mb-6 leading-tight">
            CUET UG Mock Test Free
          </h1>
          <p className="text-xl text-text-muted mb-4">
            Attempt Real Exam-Level Tests Online with Instant Score &amp; Analysis
          </p>
          <p className="text-text-muted mb-8">
            ✅ No Downloads Required &nbsp;•&nbsp; ⚡ Instant Access &nbsp;•&nbsp; 📋 Based on Latest CUET Pattern
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={handleStart} className="btn-primary px-8 py-4 text-lg font-bold rounded-xl">
              🚀 Start Free Mock Test
            </button>
            <Link to="/register" className="px-8 py-4 text-lg font-bold rounded-xl border border-glass-border text-text-light hover:bg-white/5 transition-colors">
              Register &amp; Begin Now
            </Link>
          </div>
          <p className="text-xs text-accent-gold mt-4">⚡ Limited free access available</p>
        </div>
      </div>

      {/* Quick Start */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
        <h2 className="text-3xl font-bold gradient-text text-center mb-10">Start Your Free CUET UG Mock Test Instantly</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {testTypes.map(t => (
            <button key={t.title} onClick={handleStart}
              className="glass-card text-left hover:border-primary/50 transition-colors relative group">
              <span className="absolute -top-3 right-4 px-3 py-1 bg-primary/20 text-primary text-xs font-bold rounded-full">
                {t.badge}
              </span>
              <div className="text-4xl mb-4">{t.icon}</div>
              <h3 className="text-text-light font-bold mb-2">{t.title}</h3>
              <p className="text-text-muted text-sm mb-4">{t.desc}</p>
              <span className="text-primary text-sm font-semibold group-hover:underline">Begin Test →</span>
            </button>
          ))}
        </div>
        <p className="text-center text-text-muted text-sm mt-6">
          👉 Register in just 10 seconds to unlock your test and track your score
        </p>
      </div>

      {/* Why BESST mock tests */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
        <h2 className="text-3xl font-bold gradient-text text-center mb-10">Why Our CUET UG Free Mock Tests Stand Out</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
          {features.map(f => (
            <div key={f.title} className="glass-card">
              <div className="text-3xl mb-3">{f.icon}</div>
              <h3 className="text-text-light font-bold mb-1 text-sm">{f.title}</h3>
              <p className="text-text-muted text-xs">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* How mock tests help */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
        <h2 className="text-2xl font-bold gradient-text text-center mb-8">
          Boost Your CUET UG Preparation with Smart Practice
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
          {[
            'Understand the real exam pattern',
            'Improve time management skills',
            'Identify weak topics quickly',
            'Build confidence before the exam',
            'Track your progress over time',
            'Consistent practice = Higher score',
          ].map((b, i) => (
            <div key={b} className="glass-card text-center p-4">
              <div className="text-2xl font-bold text-primary mb-2">0{i + 1}</div>
              <p className="text-text-muted text-sm">{b}</p>
            </div>
          ))}
        </div>
      </div>

      {/* What is CUET UG Mock Test */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
        <div className="glass-card max-w-3xl mx-auto">
          <h2 className="text-xl font-bold text-text-light mb-4">📝 What is a CUET UG Mock Test?</h2>
          <p className="text-text-muted text-sm leading-relaxed mb-4">
            A CUET UG mock test is a practice test that replicates the actual CUET exam pattern, giving students a real-time exam experience. It is one of the most effective ways to crack CUET UG.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {['Practice anytime, anywhere', 'Evaluate your preparation level', 'Improve accuracy and speed'].map(b => (
              <div key={b} className="flex items-center gap-2 p-3 bg-bg-secondary rounded-xl text-sm">
                <span className="text-primary">→</span>
                <span className="text-text-light">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Who is it for */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
        <div className="glass-card max-w-2xl mx-auto text-center">
          <h2 className="text-xl font-bold text-text-light mb-6">🎯 Ideal for All CUET UG Aspirants</h2>
          <div className="flex flex-wrap gap-3 justify-center">
            {['First-Time CUET Aspirants', 'Repeat Candidates', 'Students Targeting Top Universities', 'Last-Minute Revision', 'All Subject Streams'].map(t => (
              <span key={t} className="px-4 py-2 bg-primary/20 text-primary rounded-full text-sm font-semibold">{t}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Cross-sell */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
        <h2 className="text-2xl font-bold gradient-text text-center mb-6">Combine Mock Tests with Other Resources</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
          {[
            { label: 'Study Materials', href: '/study-materials', icon: '📚' },
            { label: 'PYQs', href: '/pyq-cuet', icon: '📄' },
            { label: 'Current Affairs', href: '/current-affairs-test', icon: '🌍' },
            { label: 'Counselling', href: '/cuet-counselling', icon: '🎓' },
          ].map(l => (
            <Link key={l.label} to={l.href} className="glass-card text-center hover:border-primary/50 transition-colors p-4">
              <div className="text-3xl mb-2">{l.icon}</div>
              <div className="text-text-light text-sm font-semibold">{l.label}</div>
            </Link>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
        <h2 className="text-2xl font-bold gradient-text text-center mb-8">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((f, i) => (
            <details key={i} className="glass-card">
              <summary className="font-semibold text-text-light cursor-pointer list-none flex justify-between items-center">
                {f.q} <span className="text-primary text-xl">+</span>
              </summary>
              <p className="text-text-muted mt-3 text-sm">{f.a}</p>
            </details>
          ))}
        </div>
      </div>

      {/* Final CTA */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
        <div className="glass-card text-center max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-text-light mb-3">
            Improve Your CUET UG Score with Free Mock Tests
          </h2>
          <p className="text-text-muted mb-2">
            Don't wait for exam day to test your preparation — start now and get ahead of the competition.
          </p>
          <p className="text-accent-gold text-sm mb-6">⚡ Limited free access available</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={handleStart} className="btn-primary px-8 py-3 font-bold rounded-xl">
              🚀 Take Free Mock Test Now
            </button>
            <Link to="/register"
              className="px-8 py-3 font-bold rounded-xl border border-glass-border text-text-light hover:bg-white/5 transition-colors">
              Register to Unlock Full Access
            </Link>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default CuetUgMockTestPage;
